import { useEffect, useMemo, useState } from "react";
import { Drawer, Classes } from "@blueprintjs/core";
import { isEqual } from "lodash";
import InputField from "../../components/shared/input-field";
import {
  Box,
  CustomCard,
  TitleComponent,
  Avatar,
  CustomButton,
  useDarkMode,
  CustomDatePicker,
} from "ui-components";
import {
  userProfileQuery,
  UserT,
} from "ui-io/queries/users/user-profile-query";
import { updateUserProfile } from "ui-io/queries/users/update-user-profile";
import { changeUserPassword } from "ui-io/queries/users/change-user-password";
import styled from "styled-components";
import { useMutation, useQuery, useQueryClient } from "react-query";
import useSession from "ui-io/use-session";
import toasts from "../../utils/toasts";

const MyProfile = () => {
  const { session, logout } = useSession();
  const [darkMode] = useDarkMode();
  const queryClient = useQueryClient();
  const [originalUserData, setOriginalUserData] = useState<Record<string, any>>(
    {},
  );
  const [userDetail, setUserDetail] = useState<Record<string, any>>({});
  const [isPasswordDrawerOpen, setIsPasswordDrawerOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const userConnection = useQuery(
    session?.userId
      ? userProfileQuery(session?.userId)
      : { enabled: !!session?.userId },
  );

  const updateProfileMutation = useMutation(updateUserProfile, {
    onSuccess: () => {
      toasts.success("Successfully updated the profile!");
      queryClient.refetchQueries(["GET_USER_PROFILE_DETAIL", session?.userId]);
    },
    onError: () => {
      toasts.error("Failed to update the profile!");
    },
  });

  const changePasswordMutation = useMutation(changeUserPassword, {
    onSuccess: () => {
      toasts.success("Password changed successfully!");
      setPasswordForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setIsPasswordDrawerOpen(false);
    },
    onError: () => {
      toasts.error("Failed to change password!");
    },
  });

  useEffect(() => {
    if (userConnection.data) {
      const deepCopy = JSON.parse(JSON.stringify(userConnection.data ?? {}));
      setOriginalUserData(deepCopy);
      setUserDetail(JSON.parse(JSON.stringify(userConnection.data ?? {})));
    }
  }, [userConnection.data]);

  // 3. Helper function to check if the data has changed
  // const isDirty = JSON.stringify(originalUserData) !== JSON.stringify(userDetail);
  const isDirty = !isEqual(originalUserData, userDetail);
  console.log("isDirty = = = = ", isDirty);
  const onSubmitProfile = (event: any) => {
    if (!isDirty) return;
    updateProfileMutation.mutate({
      userId: session?.userId || "",
      userData: { ...userDetail } as UserT,
    });
  };

  const handleOnChange = (event: any) => {
    const { name, value } = event.target;

    const keys = name.split(".");
    const setNestedValue = (obj: any, keys: string[], value: any): any => {
      if (keys.length === 1) {
        return { ...obj, [keys[0]]: value };
      }
      return {
        ...obj,
        [keys[0]]: setNestedValue(obj?.[keys[0]] ?? {}, keys.slice(1), value),
      };
    };

    setUserDetail((prev) => {
      return setNestedValue(prev, keys, value);
    });
  };

  const handlePasswordFormChange = (event: any) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangePasswordSubmit = () => {
    const trimmedUserId = session?.userId || "";

    if (!trimmedUserId) {
      toasts.error("User session is not available.");
      return;
    }

    if (
      !passwordForm.oldPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      toasts.error("Please fill in all password fields.");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toasts.error("New password and confirm password do not match.");
      return;
    }

    changePasswordMutation.mutate({
      userId: trimmedUserId,
      oldPassword: passwordForm.oldPassword,
      newPassword: passwordForm.newPassword,
      confirmPassword: passwordForm.confirmPassword,
    });
  };

  const isPasswordSubmitDisabled =
    !passwordForm.oldPassword ||
    !passwordForm.newPassword ||
    !passwordForm.confirmPassword ||
    changePasswordMutation.isLoading;

  return (
    <CustomCard padding="30px 60px">
      <Box flexDir="row" justify="between" items="center">
        <TitleComponent fontSize="24px">Edit Profile </TitleComponent>
        <CustomButton
          rightIcon="log-out"
          onClick={logout}
          pd="5px 20px"
          bradius="5px"
        >
          Logout
        </CustomButton>
      </Box>

      <Box flexDir="row" gap="large">
        <Box width={"auto"} justify="center">
          <ProfileImageContainer>
            <Avatar
              profileUrl={userDetail?.profileImageUrl || "/images/profile.svg"}
              displayName={"Kishan Madan"}
              email={"yantrix@gmail.com"}
              isCircle={true}
              isCurrentUser={false}
              isLoading={false}
              size={"120px"}
            />
            <PencilIcon>
              <img
                src="/icons/pencil.svg"
                alt="profile edit"
                width="25"
                height="25"
              />
            </PencilIcon>
          </ProfileImageContainer>
        </Box>
        <Box width={"1/2"}>
          <InputField
            label={"Your Name"}
            name={"name"}
            type={"text"}
            placeholder={"Kishan Madan"}
            onChange={handleOnChange}
            value={userDetail?.name ?? ""}
          />
          <InputField
            label={"Designation"}
            name={"designation"}
            type={"text"}
            placeholder={"Head of Technology"}
            onChange={handleOnChange}
            value={userDetail?.designation ?? ""}
          />
          <InputField
            label={"Company Email"}
            name={"email"}
            type={"text"}
            placeholder={"kishanmadan@gmail.com"}
            onChange={handleOnChange}
            value={userDetail?.email ?? ""}
          />
          <InputField
            label={"Date of Birth"}
            name={"dob"}
            type={"text"}
            placeholder={"02/02/1999"}
            onChange={handleOnChange}
            value={userDetail?.dob ?? ""}
          />
          {/* <CustomDatePicker
            key={"dob"}
            onValueChange={(dates) => {
              handleOnChange({ target: { name: "dob", value: dates } });
            }}
            value={userDetail?.dob ?? ""}
          /> */}

          <InputField
            label={"City"}
            name={"address.city"}
            type={"text"}
            placeholder={"Enter City"}
            onChange={handleOnChange}
            value={userDetail?.address?.city ?? ""}
          />
        </Box>

        <Box width={"1/2"}>
          <InputField
            label={"User Name"}
            name={"username"}
            type={"text"}
            placeholder={"User Name"}
            onChange={handleOnChange}
            value={userDetail?.username ?? ""}
          />
          <InputField
            label={"Company Name"}
            name={"orgName"}
            type={"text"}
            placeholder={"Apple, NY"}
            onChange={handleOnChange}
            value={userDetail?.orgName ?? ""}
          />
          <InputField
            label={"Phone Number"}
            name={"phoneNumber"}
            type={"text"}
            placeholder={"9898989898"}
            onChange={handleOnChange}
            value={userDetail?.phoneNumber ?? ""}
          />
          <InputField
            label={"Password"}
            name={"password"}
            type={"password"}
            placeholder={"********"}
            onChange={handleOnChange}
            value={userDetail?.password ?? ""}
            disabled
          />
          <PasswordLink onClick={() => setIsPasswordDrawerOpen(true)}>
            Change Password
          </PasswordLink>

          <InputField
            label={"Country"}
            name={"address.country"}
            type={"text"}
            placeholder={"USA"}
            onChange={handleOnChange}
            value={userDetail?.address?.country ?? ""}
          />
        </Box>
      </Box>

      <Box justify="end" mt="large">
        <CustomButton onClick={onSubmitProfile} disabled={!isDirty} pd="7px 30px">
          Save
        </CustomButton>
      </Box>

      <CustomDrawer
        isOpen={isPasswordDrawerOpen}
        onClose={() => setIsPasswordDrawerOpen(false)}
        title="Change Password"
        icon="key"
        className={darkMode ? Classes.DARK : ""}
        canOutsideClickClose={false}
        size="420px"
      >
        <DrawerBody>
          <InputField
            label={"Old Password"}
            name={"oldPassword"}
            type={"password"}
            placeholder={"Enter your old password"}
            onChange={handlePasswordFormChange}
            value={passwordForm.oldPassword}
          />
          <InputField
            label={"New Password"}
            name={"newPassword"}
            type={"password"}
            placeholder={"Enter new password"}
            onChange={handlePasswordFormChange}
            value={passwordForm.newPassword}
          />
          <InputField
            label={"Confirm Password"}
            name={"confirmPassword"}
            type={"password"}
            placeholder={"Confirm new password"}
            onChange={handlePasswordFormChange}
            value={passwordForm.confirmPassword}
          />

          <DrawerActions>
            <CancelButton onClick={() => setIsPasswordDrawerOpen(false)}>
              Cancel
            </CancelButton>
            <SavePasswordButton
              onClick={handleChangePasswordSubmit}
              disabled={isPasswordSubmitDisabled}
            >
              {changePasswordMutation.isLoading
                ? "Updating..."
                : "Update Password"}
            </SavePasswordButton>
          </DrawerActions>
        </DrawerBody>
      </CustomDrawer>
    </CustomCard>
  );
};
export default MyProfile;

const PasswordLink = styled.a`
  display: inline-block;
  margin-top: 6px;
  margin-bottom: 12px;
  color: #2d72d2;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  float: right;
`;

const PencilIcon = styled.div`
  position: absolute;
  bottom: 10px;
  right: 0;
  cursor: not-allowed;
`;

const ProfileImageContainer = styled.div`
  position: relative;
  height: fit-content;
  margin-top: 30px;
`;

const CustomDrawer = styled(Drawer)`
  .bp5-drawer {
    border-radius: 14px;
    overflow: hidden;
  }
`;

const DrawerBody = styled.div`
  padding: 20px;
`;

const DrawerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const CancelButton = styled(CustomButton)`
  background-color: transparent !important;
  border: 1px solid #5f6b7c !important;
`;

const SavePasswordButton = styled(CustomButton)`
  background-color: #2d72d2 !important;
  color: #fff !important;
`;
